import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Loader2,
  Download,
  ShieldCheck,
  Flame,
  Droplets,
  PackageCheck,
  Leaf,
  CheckCircle2,
} from "lucide-react";
import { Eyebrow, FadeUp } from "../components/site/Primitives";
import ProductCard from "../components/site/ProductCard";
import ProductImage from "../components/site/ProductImage";
import { fetchProduct, productSpecPdfUrl } from "../lib/api";
import { useProducts } from "../hooks/useProducts";

const QUALITY_ICON_MAP = {
  "100% Compostable": Leaf,
  "PFAS Free": ShieldCheck,
  "Food Grade Certified": CheckCircle2,
  "Microwave Safe": Flame,
  "Oil & Water Resistant": Droplets,
  "Strong & Leak Proof": PackageCheck,
};

const STANDARDS = [
  "100% Compostable",
  "PFAS Free",
  "Food Grade Certified",
  "Microwave Safe",
  "Oil & Water Resistant",
  "Strong & Leak Proof",
];

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products } = useProducts();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const run = async () => {
      try {
        const p = await fetchProduct(id);
        if (!active) return;
        queueMicrotask(() => {
          setProduct(p);
          setLoading(false);
        });
      } catch (e) {
        if (!active) return;
        queueMicrotask(() => {
          setError(e?.response?.status === 404 ? "not-found" : "error");
          setLoading(false);
        });
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#F7F5F0]/60">
        <Loader2 className="h-6 w-6 animate-spin mr-3 text-[#E85D2F]" />
        Loading product…
      </div>
    );
  }

  if (error === "not-found" || !product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <p className="font-grotesk text-[#E85D2F] tracking-[0.25em] text-xs uppercase">404 · Not found</p>
        <h1 className="mt-6 font-grotesk text-5xl md:text-6xl font-black tracking-tighter text-[#F7F5F0]">
          We can&rsquo;t find that product.
        </h1>
        <button
          onClick={() => navigate("/products")}
          data-testid="back-to-catalogue"
          className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-semibold hover:bg-[#F47A4A] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to catalogue
        </button>
      </div>
    );
  }

  const related = products.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 3);

  return (
    <div data-testid="product-detail-page" className="bg-[#0A0A09]">
      {/* Breadcrumb */}
      <section className="pt-8">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#F7F5F0]/50">
          <Link to="/products" className="hover:text-[#E85D2F] transition-colors flex items-center gap-2">
            <ArrowLeft className="h-3 w-3" />
            Catalogue
          </Link>
          <span className="text-[#F7F5F0]/30">/</span>
          <span className="text-[#E85D2F]">{product.category}</span>
        </div>
      </section>

      {/* Hero split */}
      <section className="py-12 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Image */}
          <FadeUp className="lg:col-span-6">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#F2EEE3]">
              <ProductImage
                product={product}
                eager
                sizes="(min-width: 1024px) 50vw, 100vw"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="product-detail-image"
              />
              <div className="absolute top-5 left-5">
                <span className="rounded-full bg-[#0A0A09]/80 backdrop-blur text-[#E85D2F] text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-semibold border border-[#E85D2F]/30">
                  {product.category}
                </span>
              </div>
            </div>
          </FadeUp>

          {/* Info */}
          <div className="lg:col-span-6">
            <FadeUp>
              <Eyebrow>Product spec sheet</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h1
                data-testid="product-detail-name"
                className="mt-6 font-grotesk text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.035em] leading-[0.95] text-[#F7F5F0]"
              >
                {product.name}
                <span className="text-[#E85D2F]">.</span>
              </h1>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="mt-4 text-lg text-[#E85D2F] font-medium">{product.tagline}</p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p className="mt-6 text-base md:text-lg text-[#F7F5F0]/75 leading-relaxed font-light">
                {product.description}
              </p>
            </FadeUp>

            <FadeUp delay={0.3}>
              <dl className="mt-10 grid sm:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-[#E85D2F]/15 bg-[#121211] p-5">
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-[#E85D2F] font-semibold mb-2">
                    Material
                  </dt>
                  <dd className="text-[#F7F5F0] font-medium leading-snug">{product.material}</dd>
                </div>
                <div className="rounded-2xl border border-[#E85D2F]/15 bg-[#121211] p-5">
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-[#E85D2F] font-semibold mb-2">
                    Specifications
                  </dt>
                  <dd className="text-[#F7F5F0] font-medium leading-snug">{product.specs || "—"}</dd>
                </div>
                <div className="rounded-2xl border border-[#E85D2F]/15 bg-[#121211] p-5 sm:col-span-2">
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-[#E85D2F] font-semibold mb-2">
                    Ideal use
                  </dt>
                  <dd className="text-[#F7F5F0] font-medium leading-snug">{product.idealUse}</dd>
                </div>
              </dl>
            </FadeUp>

            <FadeUp delay={0.4}>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/contact?product=${encodeURIComponent(product.name)}`}
                  data-testid="detail-enquire"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-semibold tracking-wide hover:bg-[#F47A4A] transition-colors"
                >
                  Enquire about this product
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <motion.a
                  whileTap={{ scale: 0.97 }}
                  href={productSpecPdfUrl(product.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-download-pdf"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] px-7 py-4 font-medium tracking-wide hover:bg-[#F7F5F0] hover:text-[#0A0A09] transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download spec sheet (PDF)
                </motion.a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Quality strip */}
      <section className="bg-[#F2EEE3] text-[#0A0A09] py-16 border-y border-[#0A0A09]/5">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <span className="inline-flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#E85D2F]">
            <span className="h-px w-8 bg-[#E85D2F]" />
            Quality standards
          </span>
          <h2 className="mt-5 font-grotesk text-3xl md:text-4xl font-black tracking-tighter leading-[0.95] max-w-2xl">
            Every BlacPac product is built to the same six standards.
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {STANDARDS.map((label) => {
              const Icon = QUALITY_ICON_MAP[label] || CheckCircle2;
              return (
                <div
                  key={label}
                  className="flex flex-col items-start gap-3 rounded-2xl bg-[#0A0A09] text-[#F7F5F0] p-5"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#E85D2F] text-[#0A0A09]">
                    <Icon className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <p className="font-grotesk font-bold text-sm leading-tight">{label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-24 lg:py-32">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <Eyebrow>More from this range</Eyebrow>
                <h2 className="mt-6 font-grotesk text-3xl md:text-5xl font-black tracking-tighter leading-[0.95] text-[#F7F5F0]">
                  Other {product.category} products.
                </h2>
              </div>
              <Link
                to="/products"
                className="group inline-flex items-center gap-3 text-[#E85D2F] font-medium hover:text-[#F47A4A] transition-colors whitespace-nowrap"
              >
                View full catalogue
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {related.map((p, i) => (
                <FadeUp key={p.id} delay={i * 0.06}>
                  <ProductCard product={p} index={i} />
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
